import styled, { css } from 'styled-components/macro'
import { theme } from 'styled-tools'
import { media } from 'ui/rwd'

const HeroVisual = styled.div`
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  ${media.sm.down`
    padding: 2.25rem 2rem;
  `}
  @media (min-width: 600px) and (max-width: 767px) {
    padding: 2.5rem 5rem;
  }
  @media (min-width: 768px) and (max-width: 991px) {
    padding-left: 128px;
    padding-right: 128px;
  }
  ${media.md`
    flex: 48 1 0;
    min-width: 0;
    padding-left: 0;
    padding-right: 0;
  `}
`

const HeroSlabs = styled.div`
  position: absolute;
  inset: -2% 0;
  z-index: 0;
  pointer-events: none;
`

const HeroSlab = styled.span.attrs(props => ({
  'data-tone': props.$tone
}))`
  position: absolute;
  display: block;
  transition: transform 0.5s ease;

  ${props =>
    props.$tone === 'yellow' &&
    `
    top: 2%;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 94%;
    background-color: ${theme('yellow')(props)};
  `}

  ${props =>
    props.$tone === 'teal' &&
    `
    top: -3%;
    right: -4%;
    z-index: 1;
    width: 80%;
    height: 50%;
    background-color: ${theme('primary')(props)};
  `}

  ${props =>
    props.$tone === 'pink' &&
    `
    bottom: -3%;
    left: -4%;
    z-index: 1;
    width: 76%;
    height: 48%;
    background-color: ${theme('pink')(props)};
  `}
`

const HeroArtFrame = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 5 / 3;
  overflow: visible;

  &:hover ${HeroSlab}[data-tone='yellow'] {
    transform: scale(1.03);
  }

  &:hover ${HeroSlab}[data-tone='teal'] {
    transform: translate(6%, -12%);
  }

  &:hover ${HeroSlab}[data-tone='pink'] {
    transform: translate(-6%, 12%);
  }

  @media (prefers-reduced-motion: reduce) {
    ${HeroSlab} {
      transition: none;
    }

    &:hover ${HeroSlab} {
      transform: none;
    }
  }
`

// Widths mirror each SVG's intrinsic width so the group keeps the legacy proportions.
// Negative offsets match the original ImageArea that nested the later objects together.
const HeroObjects = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  box-sizing: border-box;
  width: 92%;
  left: 4%;

  > * {
    position: relative;
  }

  > *:nth-child(1) {
    z-index: 7;
  }
  > *:nth-child(2) {
    z-index: 6;
  }
  > *:nth-child(3) {
    z-index: 5;
  }
  > *:nth-child(4) {
    z-index: 4;
  }
  > *:nth-child(5) {
    z-index: 3;
  }
  > *:nth-child(6) {
    z-index: 2;
  }
  > *:nth-child(7) {
    z-index: 1;
  }
`

const HeroObject = styled.div`
  flex: ${props => props.$flex} 1 0;
  min-width: 0;
  ${props =>
    props.$shift &&
    css`
      position: relative;
      left: ${props.$shift};
    `}
  ${props =>
    props.$transform &&
    css`
      &:hover > img {
        transform: ${props.$transform};
      }
    `}
`

const TottenhamSlot = styled.div`
  width: 100%;

  &:hover > img {
    transform: scale(0.8);
  }
`

const BallSlot = styled.div`
  position: absolute;
  top: 0;
  right: 8%;
  z-index: 2;
  width: 40%;
  aspect-ratio: 1 / 1;

  &:hover > img {
    transform: translateY(-30px);
  }
`

const heroObject = css`
  display: block;
  width: 100%;
  min-width: 0;
  height: auto;
  transition: transform 300ms ease-in-out;
`

const Glasses = styled.img`
  ${heroObject};
  aspect-ratio: 71.25 / 175.108;
`

const Flamingo = styled.img`
  ${heroObject};
  aspect-ratio: 107.707 / 214.689;
`

const Lamp = styled.img`
  ${heroObject};
  aspect-ratio: 48.57 / 192.13;
`

const Globe = styled.img`
  ${heroObject};
  aspect-ratio: 68.288 / 82.995;
`

const TottenhamAndBall = styled.div`
  position: relative;
  flex: 117 1 0;
  min-width: 0;
  left: -1.5rem;
`

const Tottenham = styled.img`
  ${heroObject};
  aspect-ratio: 117.088 / 169.825;
`

const Ball = styled.img`
  ${heroObject};
  aspect-ratio: 1 / 1;
`

const Guitar = styled.img`
  ${heroObject};
  aspect-ratio: 88.783 / 179.186;
`

const Cactus = styled.img`
  ${heroObject};
  aspect-ratio: 83.615 / 210.611;
`

export {
  HeroVisual,
  HeroArtFrame,
  HeroSlabs,
  HeroSlab,
  HeroObjects,
  HeroObject,
  TottenhamSlot,
  BallSlot,
  Glasses,
  Flamingo,
  Lamp,
  Globe,
  TottenhamAndBall,
  Tottenham,
  Ball,
  Guitar,
  Cactus
}
